export interface DurableCheckpoint<TPayload> {
  readonly key: string;
  readonly schemaVersion: number;
  readonly businessDate: string;
  readonly revision: number;
  readonly payload: TPayload;
  readonly committedAt: string;
}

export interface CheckpointCommitInput<TPayload> {
  readonly key: string;
  readonly schemaVersion: number;
  readonly businessDate: string;
  readonly payload: TPayload;
  readonly committedAt: string;
}

export interface OutboxRecord {
  readonly id: string;
  readonly kind: string;
  readonly payload: unknown;
  readonly createdAt: string;
  readonly status: 'pending' | 'acknowledged';
  readonly acknowledgedAt?: string;
}

export interface DurabilityPort {
  loadCheckpoint<TPayload>(key: string): Promise<DurableCheckpoint<TPayload> | null>;
  saveCheckpoint<TPayload>(
    expectedRevision: number,
    checkpoint: DurableCheckpoint<TPayload>,
  ): Promise<{ readonly ok: true } | { readonly ok: false; readonly code: 'REVISION_CONFLICT'; readonly actualRevision: number }>;
  enqueue(record: OutboxRecord): Promise<void>;
  pending(): Promise<readonly OutboxRecord[]>;
  acknowledge(id: string, at: string): Promise<boolean>;
  findOutbox(id: string): Promise<OutboxRecord | null>;
}

const clone = <T>(value: T): T => structuredClone(value);

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
};

const signatureOf = (value: unknown): string => JSON.stringify(stableValue(value));
const outboxIdentitySignature = (record: Pick<OutboxRecord, 'id' | 'kind' | 'payload' | 'createdAt'>): string =>
  signatureOf({ id: record.id, kind: record.kind, payload: record.payload, createdAt: record.createdAt });

const assertText = (value: string, code: string): void => {
  if (!value?.trim()) throw new Error(code);
};

const assertTimestamp = (value: string, code: string): void => {
  if (!Number.isFinite(Date.parse(value))) throw new Error(code);
};

const assertSchemaVersion = (value: number): void => {
  if (!Number.isSafeInteger(value) || value <= 0) throw new Error('DURABILITY_SCHEMA_VERSION_INVALID');
};

const validateOutbox = (record: OutboxRecord): void => {
  assertText(record.id, 'DURABILITY_OUTBOX_ID_REQUIRED');
  assertText(record.kind, 'DURABILITY_OUTBOX_KIND_REQUIRED');
  assertTimestamp(record.createdAt, 'DURABILITY_OUTBOX_CREATED_AT_INVALID');
  if (record.status !== 'pending' && record.status !== 'acknowledged') throw new Error('DURABILITY_OUTBOX_STATUS_INVALID');
  if (record.status === 'acknowledged') {
    if (!record.acknowledgedAt) throw new Error('DURABILITY_OUTBOX_ACK_AT_REQUIRED');
    assertTimestamp(record.acknowledgedAt, 'DURABILITY_OUTBOX_ACK_AT_INVALID');
  }
};

export function createDurabilityCoordinator(port: DurabilityPort) {
  if (typeof port.findOutbox !== 'function') throw new Error('DURABILITY_FIND_OUTBOX_REQUIRED');
  const knownOutbox = new Map<string, string>();

  return {
    async commit<TPayload>(
      input: CheckpointCommitInput<TPayload>,
      expectedRevision: number,
    ): Promise<DurableCheckpoint<TPayload>> {
      assertText(input.key, 'DURABILITY_CHECKPOINT_KEY_REQUIRED');
      assertSchemaVersion(input.schemaVersion);
      assertText(input.businessDate, 'DURABILITY_BUSINESS_DATE_REQUIRED');
      assertTimestamp(input.committedAt, 'DURABILITY_COMMITTED_AT_INVALID');
      if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 0) {
        throw new Error('DURABILITY_EXPECTED_REVISION_INVALID');
      }

      const checkpoint: DurableCheckpoint<TPayload> = {
        ...clone(input),
        revision: expectedRevision + 1,
      };
      const result = await port.saveCheckpoint(expectedRevision, checkpoint);
      if (!result.ok) throw new Error(`DURABILITY_REVISION_CONFLICT:${result.actualRevision}`);
      return clone(checkpoint);
    },

    async restore<TPayload>(key: string, supportedSchemaVersion: number): Promise<DurableCheckpoint<TPayload> | null> {
      assertText(key, 'DURABILITY_CHECKPOINT_KEY_REQUIRED');
      assertSchemaVersion(supportedSchemaVersion);
      const checkpoint = await port.loadCheckpoint<TPayload>(key);
      if (!checkpoint) return null;
      if (checkpoint.key !== key) throw new Error('DURABILITY_CHECKPOINT_KEY_MISMATCH');
      if (checkpoint.schemaVersion !== supportedSchemaVersion) {
        throw new Error(`DURABILITY_SCHEMA_UNSUPPORTED:${checkpoint.schemaVersion}`);
      }
      return clone(checkpoint);
    },

    async enqueue(record: OutboxRecord): Promise<OutboxRecord> {
      validateOutbox(record);
      if (record.status !== 'pending') throw new Error('DURABILITY_OUTBOX_MUST_START_PENDING');
      const signature = outboxIdentitySignature(record);
      const remembered = knownOutbox.get(record.id);
      if (remembered && remembered !== signature) throw new Error('DURABILITY_OUTBOX_ID_CONFLICT');

      const existing = await port.findOutbox(record.id);
      if (existing) {
        validateOutbox(existing);
        const existingSignature = outboxIdentitySignature(existing);
        knownOutbox.set(record.id, existingSignature);
        if (existingSignature !== signature) throw new Error('DURABILITY_OUTBOX_ID_CONFLICT');
        return clone(existing);
      }

      await port.enqueue(clone(record));
      knownOutbox.set(record.id, signature);
      return clone(record);
    },

    async pending(): Promise<readonly OutboxRecord[]> {
      const records = await port.pending();
      for (const record of records) validateOutbox(record);
      return records.map((record) => clone(record));
    },

    async acknowledge(id: string, at: string): Promise<void> {
      assertText(id, 'DURABILITY_OUTBOX_ID_REQUIRED');
      assertTimestamp(at, 'DURABILITY_OUTBOX_ACK_AT_INVALID');
      if (!await port.acknowledge(id, at)) throw new Error('DURABILITY_OUTBOX_NOT_FOUND');
    },
  };
}
